import Vue from 'vue'
import { parseAxiosException } from '@/helpers/exceptionParser.js'
import { BillingApiModel, InvoiceApiModel } from './BusinessUnitApiModel'

const buildModule = apiClient => ({
  namespaced: true,

  state: {
    billing: null,
    invoices: null,
    apiResponse: null,
    isFetching: false,
    fetchError: null,
    isSaving: false,
    saveError: null,
    isDownloading: false,
    downloadError: null,
  },

  mutations: {
    setBilling: (state, payload) => Vue.set(state, 'billing', payload),
    setInvoices: (state, payload) => Vue.set(state, 'invoices', payload),
    setIsFetching: (state, payload) => (state.isFetching = payload),
    setFetchError: (state, payload) => (state.fetchError = payload),
    setApiResponse: (state, payload) => (state.apiResponse = payload),
    setIsSaving: (state, payload) => (state.isSaving = payload),
    setSaveError: (state, payload) => (state.saveError = payload),
    setIsDownloading: (state, payload) => (state.isDownloading = payload),
    setDownloadError: (state, payload) => (state.downloadError = payload),
  },

  actions: {
    async fetchBusinessUnitBillingData({ commit }, businessUnitKey) {
      commit('setIsFetching', true)
      commit('setFetchError', null)
      try {
        const response = await apiClient.get(`settings/application/billing/${businessUnitKey}`)
        commit('setApiResponse', response)
        const model = new BillingApiModel(response.data)
        commit('setBilling', model)
      } catch (e) {
        console.error(e)
        commit('setFetchError', {
          rawError: e,
          parsedError: parseAxiosException(e),
        })
      }
      commit('setIsFetching', false)
    },
    async saveCustomerInChargebee({ commit }, businessBillingData) {
      const model = new BillingApiModel(businessBillingData)
      commit('setIsSaving', true)
      commit('setSaveError', null)
      let success = true

      try {
        const response = await apiClient.post(
          `settings/application/billing/${model.BusinessUnitKey}`,
          model,
        )
        commit('setApiResponse', response)
        commit('setBilling', response.data)
      } catch (e) {
        commit('setSaveError', e)
        success = false
      }
      commit('setIsSaving', false)
      return success
    },
    async fetchInvoices({ commit }) {
      commit('setIsFetching', true)
      commit('setFetchError', null)
      try {
        const response = await apiClient.get('billing/invoice')
        commit('setApiResponse', response)
        commit('setInvoices', response.data)
      } catch (e) {
        console.error(e)
        commit('setFetchError', e)
      }
      commit('setIsFetching', false)
    },
    async dowloadInvoice({ commit }, invoiceData) {
      commit('setIsDownloading', true)
      commit('setDownloadError', null)
      const { invoiceKey, type } = invoiceData
      let response = {
        status: null,
        data: null,
        message: null,
        success: false,
      }
      if (invoiceKey && type && (type === 'pdf' || type === 'specification')) {
        const contentType = type === 'pdf' ? 'application/pdf' : 'text/csv'
        try {
          response = await apiClient.get(`billing/invoice/${invoiceKey}/${type}`, {
            headers: {
              Accept: contentType,
              'Content-Type': contentType,
            },
            responseType: 'blob',
          })
          response.message = 'Successfully downloaded'
          response.success = true
        } catch (e) {
          const parsedError = parseAxiosException(e)
          commit('setDownloadError', {
            rawError: e,
            parsedError,
          })
          response.status = parsedError.status
          response.message =
            parsedError.status === 404 ? 'Invoice Not Found' : 'Something went wrong!'
        }
      } else {
        const message = 'Invoice key and Document type are required'
        commit('setDownloadError', {
          message,
        })
        response.message = message
      }
      commit('setIsDownloading', false)
      return response
    },
  },

  getters: {
    isPending: state => state.isSaving || state.isFetching,
    isFetching: state => state.isFetching,
    isSaving: state => state.isSaving,
    isDownloading: state => state.isDownloading,
    downloadError: state => state.downloadError,
    businessUnitKey: state => state.billing?.BusinessUnitKey,
    accontCreatedInBillingSystem: state => state.billing?.AccontCreatedInBillingSystem,
    availableProducts: state => state.billing?.AvailableProducts,
    selectedBillingProductCode: state => state.billing?.SelectedBillingProductCode,
    generalContactInformation: state => state.billing?.GeneralContactInformation,
    financialContactInformation: state => state.billing?.FinancialContactInformation,
  },
})

export default buildModule
